import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Send, Users, FileText, CheckCircle2, XCircle, Clock, Calculator, TrendingDown } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ClientDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [analyticsRes, campaignsRes] = await Promise.all([
        axios.get(`${API_URL}/analytics/dashboard`),
        axios.get(`${API_URL}/campaigns`)
      ]);
      setAnalytics(analyticsRes.data);
      setCampaigns(campaignsRes.data.slice(0, 5));
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64" data-testid="loading">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-700"></div>
        </div>
      </Layout>
    );
  }

  const statusData = analytics?.messages_by_status ? [
    { name: 'Envoyés', value: analytics.messages_by_status.sent, color: '#6366f1' },
    { name: 'Livrés', value: analytics.messages_by_status.delivered, color: '#22c55e' },
    { name: 'Lus', value: analytics.messages_by_status.read, color: '#3b82f6' },
    { name: 'Échoués', value: analytics.messages_by_status.failed, color: '#ef4444' }
  ] : [];

  return (
    <Layout>
      <div className="space-y-8" data-testid="client-dashboard">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Tableau de bord
          </h1>
          <p className="mt-2 text-slate-600">Vue d'ensemble de vos campagnes RCS</p>
        </div>

        {/* Cost Simulator Widget */}
        <Card className="rounded-2xl border-indigo-100 bg-gradient-to-br from-indigo-50 to-purple-50">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="rounded-lg bg-indigo-100 p-2">
                    <Calculator className="h-6 w-6 text-indigo-700" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Optimisez vos Coûts RCS
                  </h3>
                </div>
                <p className="text-slate-600 mb-4">
                  Utilisez notre simulateur pour comparer les modes conversationnel et non-conversationnel. 
                  Économisez jusqu'à <span className="font-bold text-green-600">60-80%</span> sur vos campagnes !
                </p>
                <div className="flex items-center space-x-4">
                  <Button
                    onClick={() => navigate('/cost-simulator')}
                    className="rounded-full bg-indigo-700 font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                    data-testid="open-simulator-button"
                  >
                    <Calculator className="mr-2 h-5 w-5" />
                    Simuler mes coûts
                  </Button>
                  <div className="flex items-center text-green-600">
                    <TrendingDown className="h-5 w-5 mr-1" />
                    <span className="text-sm font-medium">Économies garanties</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid - Bento Layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          <Card className="rounded-2xl border-slate-100 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Campagnes</p>
                  <p className="mt-2 text-3xl font-bold text-slate-900" data-testid="total-campaigns">
                    {analytics?.total_campaigns || 0}
                  </p>
                </div>
                <div className="rounded-xl bg-indigo-50 p-3">
                  <Send className="h-8 w-8 text-indigo-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-slate-100 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Messages</p>
                  <p className="mt-2 text-3xl font-bold text-slate-900" data-testid="total-messages">
                    {analytics?.total_messages || 0}
                  </p>
                </div>
                <div className="rounded-xl bg-blue-50 p-3">
                  <FileText className="h-8 w-8 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-slate-100 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Contacts</p>
                  <p className="mt-2 text-3xl font-bold text-slate-900" data-testid="total-contacts">
                    {analytics?.total_contacts || 0}
                  </p>
                </div>
                <div className="rounded-xl bg-purple-50 p-3">
                  <Users className="h-8 w-8 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-slate-100 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Taux de livraison</p>
                  <p className="mt-2 text-3xl font-bold text-green-600" data-testid="delivery-rate">
                    {analytics?.total_messages > 0
                      ? Math.round((analytics.messages_by_status.delivered / analytics.total_messages) * 100)
                      : 0}%
                  </p>
                </div>
                <div className="rounded-xl bg-green-50 p-3">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="rounded-2xl border-slate-100 col-span-1">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Statut des messages
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.name}: ${entry.value}`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-slate-100 col-span-1">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Dernières campagnes
              </h3>
              <div className="space-y-3">
                {campaigns.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">Aucune campagne</p>
                ) : (
                  campaigns.map((campaign) => (
                    <div
                      key={campaign.id}
                      className="flex items-center justify-between rounded-lg border border-slate-100 p-3 hover:bg-slate-50 transition-colors"
                      data-testid={`campaign-${campaign.id}`}
                    >
                      <div>
                        <p className="font-medium text-slate-900">{campaign.name}</p>
                        <p className="text-xs text-slate-500">
                          {campaign.total_recipients} destinataires
                        </p>
                      </div>
                      <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                        campaign.status === 'sent' ? 'bg-green-50 text-green-700' :
                        campaign.status === 'active' ? 'bg-blue-50 text-blue-700' :
                        'bg-yellow-50 text-yellow-700'
                      }`}>
                        {campaign.status === 'sent' ? 'Envoyé' : campaign.status === 'active' ? 'Actif' : 'Brouillon'}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default ClientDashboard;